import React, { useState, useMemo } from 'react';
import { Play } from 'lucide-react';
import styles from './GameGrid.module.css';

/**
 * GameGrid Component
 * Grid/List view of games
 * Uses the same game card rendering approach as GameCards.jsx
 * Handles image errors properly - filters out games with broken images
 */
const GameGrid = ({ games, viewMode, onGameClick, isLoading }) => {
  const [failedImages, setFailedImages] = useState(new Set());

  // Filter out games with failed images or invalid image URLs
  const validGames = useMemo(() => {
    return games.filter((game) => {
      // Skip items where image has failed to load
      const gameId = game.id || game.uuid || game.title;
      if (failedImages.has(gameId)) {
        return false;
      }
      
      // Skip items with invalid or missing image URLs
      const gameIcon = game.game_icon || game.image || '';
      if (!gameIcon || 
          gameIcon === null ||
          gameIcon === '/assets/images/default_card3.png' ||
          gameIcon === '/assets/images/placeholder.png' ||
          gameIcon.trim() === '' ||
          gameIcon === 'undefined' ||
          gameIcon === 'null') {
        return false;
      }
      
      // Validate image URL format
      const validImageUrl = gameIcon && 
                           (gameIcon.startsWith('http') || gameIcon.startsWith('/')) &&
                           !gameIcon.includes('undefined') &&
                           !gameIcon.includes('null');
      
      return validImageUrl;
    });
  }, [games, failedImages]);

  // Handle image load errors
  const handleImageError = (gameId, event) => {
    // Prevent infinite loop by checking if we've already marked this image as failed
    if (!failedImages.has(gameId)) {
      setFailedImages((prev) => new Set([...prev, gameId]));
      // Set placeholder to prevent broken image icon
      if (event.target) {
        event.target.src = '/assets/images/default_card3.png';
        // Hide the image if placeholder also fails
        event.target.onerror = () => {
          event.target.style.display = 'none';
        };
      }
    }
  };

  if (isLoading && validGames.length === 0) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loader}>
          <span className={styles.loaderText}>loading</span>
          <span className={styles.load}></span>
        </div>
      </div>
    );
  }

  if (validGames.length === 0) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyIcon}>🎮</div>
        <h3 className={styles.emptyTitle}>No games found</h3>
        <p className={styles.emptyMessage}>
          Try adjusting your search or filters to find more games.
        </p>
      </div>
    );
  }

  return (
    <div className={`${styles.gameGrid} ${styles[viewMode]}`}>
      {validGames.map((game) => {
        const gameId = game.id || game.uuid || game.title;
        return (
          <div
            key={gameId}
            className={styles.gameCard}
            onClick={() => onGameClick(game)}
          >
            <div className={styles.gameImageContainer}>
              <img
                src={game.game_icon || game.image || '/assets/images/default_card3.png'}
                alt={game.title || game.name || game.game_name || 'Game'}
                className={styles.gameImage}
                loading="lazy"
                onError={(e) => handleImageError(gameId, e)}
              />
              <div className={styles.playOverlay}>
                <div className={styles.playButton}>
                  <Play size={24} />
                </div>
              </div>
            </div>
            {viewMode === 'list' && (
              <div className={styles.gameInfo}>
                <h3 className={styles.gameTitle}>{game.title || game.name || game.game_name}</h3>
                <div className={styles.gameMeta}>
                  <span className={styles.gameBadge}>{game.vendor || game.provider}</span>
                  {game.game_type && <span>{game.game_type}</span>}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default GameGrid;
